/**
 * This file is responsible for rendering the category page in the application.
 * It fetches and displays a specific category and its associated items.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the category and its items.
 * If the category does not exist or there are no items in the category, a not found page is returned.
 *
 * The component also handles redirects based on the user's language and locality settings.
 * If a redirect is necessary, the user is redirected to the appropriate category page.
 *
 * The component uses the 'next/router' library to access the locale from the router.
 * This locale is used to fetch the corresponding category and items for the user's language.
 *
 * The component also handles loading states and errors during the fetching of the category and items.
 * If there's an error during the fetch, it is stored in the state and displayed to the user.
 *
 * The Category and CategoryHeader components from 'hah-fe-core' are used to render the category and its header.
 */

/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, ReactElement } from 'react';
import { GetServerSideProps } from 'next';
import { NextRouter, useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getCategoryLink, getCategory } from '@Hotels-at-Home/hah-fe-core/dist/utils/category';
import Category from '@Hotels-at-Home/hah-fe-core/dist/components/Category';
import CategoryHeader from '~/ui/pages/Category/CategoryHeader';
import Services from '~/ui/components/common/Services';
import { categoryServicesMock } from '~/ui/components/common/Services/servicesMock';
import {
  dataLayerCategoryAccor,
  dataLayerFeaturedProductsAccor,
  getCookie,
  getCurrencyCode,
  isValidCurrencyCode,
} from '@Hotels-at-Home/hah-fe-core/dist/utils';

import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';
import Head from 'next/head';
import { productNameFilters, siteName, parentHotelCompany } from '~/constants/siteConfig';
import apolloConfig from '~/constants/apolloConfig';

import {
  TServerSideProps,
  IBanner,
  IScandiCategoryChildItem,
  ICategory,
  ProductColorAttributeCode,
  IUrlResolverRequest,
  IUrlResolverResponse,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import {
  LanguageCode,
  COOKIE_LOCALITY_NAME,
  COOKIE_CURRENCY_NAME,
  COOKIE_CURRENCY_PREF,
} from '@Hotels-at-Home/hah-fe-core/dist/constants';

import {
  getHomepageRedirect,
  getRedirectCategory,
  shouldRedirect,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/redirect';
import { wrapper } from '~/store';
import {
  filterExternalHrefData,
  generateEuHrefData,
  getHrefData,
  homepageHrefLangTags,
  HrefMap,
} from '~/utils/hreflangs';
import { useTranslation } from 'next-i18next';
import languageParser from 'accept-language-parser';
import { updateCategory } from '@Hotels-at-Home/hah-fe-core/dist/store/category';
import YMALCarousel from '~/ui/components/common/YMALCarousel';
import YMALCarouselSkeleton from '~/ui/components/common/YMALCarousel/YMALCarouselSkeleton';
import CategorySEO from '~/ui/components/common/CategorySEO';
import { useItemListName, usePageViewEvent } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { publicQuery, UrlResolver } from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';

interface ICategoryPage {
  banner: IBanner;
  category: ICategory;
  categoryItems: IScandiCategoryChildItem[];
  isScandiCategoryItemsUsed: boolean;
}

interface HrefDataCategory {
  [key: string]: any;
}

type TProps = TServerSideProps<ICategoryPage> & {
  languageUrls?: {
    href: string;
    value: string;
  }[];
};

const getDynamicRoute = (router: NextRouter) => {
  const route = router.asPath.split('?')[0];
  return route;
};

const CategoryPage = (props: TProps): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '' });
  const { error, category, categoryItems, banner, languageUrls } = props;

  const router = useRouter();
  const itemListName = useItemListName(getDynamicRoute(router));
  const locale = router.locale as LanguageCode;
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

  usePageViewEvent({
    category: category.name_en.category_name,
  });
  const hrefDataCategory = getHrefData(category, locale);
  const externalHrefData = filterExternalHrefData(hrefDataCategory);
  const euHrefData = generateEuHrefData(languageUrls, siteUrl, '/c/');

  const meta = {
    title: category?.meta_title || `${t('$local.$meta.meta_title')} - ${category?.name}`,
    description: category?.meta_description || category?.description,
    keyword: category?.meta_keywords,
    url: `${siteUrl}/${locale}${router.asPath}`,
    image: category?.image || category?.seo_copy_image,
  };

  const canonicalUrl = `${siteUrl}/${locale}${new URL(router.asPath, siteUrl).pathname}`;

  const someChildrenHaveColors = (): boolean =>
    categoryItems.some(item => {
      if (item.product_data && item.product_data?.configurable_options) {
        return item.product_data.configurable_options.some(option =>
          Object.values(ProductColorAttributeCode).includes(
            option.attribute_code as ProductColorAttributeCode,
          ),
        );
      }
      return false;
    });

  useEffect(() => {
    if (!category || !categoryItems.length) return;
    dataLayerCategoryAccor({
      affiliation: parentHotelCompany,
      brand: siteName,
      categoryItems,
      siteFilters: productNameFilters,
      itemListName,
    });
  }, [categoryItems, category]);

  const itemListNameYmals = useItemListName('/suggestions/');
  useEffect(() => {
    const items = category.related_products_category;
    if (!items || !items.length) return;
    dataLayerFeaturedProductsAccor({
      brand: siteName,
      items,
      itemListName: itemListNameYmals,
    });
  }, [router.asPath]);

  return (
    <NextPage error={error} meta={meta} siteUrl={siteUrl}>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        {euHrefData.map(
          ({ href, url, rel }) =>
            href && <link key={`${href}`} rel={rel} hrefLang={href} href={url} />,
        )}
        {externalHrefData.map(
          ({ href, hreflang, rel }) =>
            href && <link key={`${hreflang} ${rel}`} rel={rel} hrefLang={hreflang} href={href} />,
        )}
      </Head>
      {category && (
        <Category
          category={category}
          categoryItems={categoryItems}
          banner={banner}
          enableColorSwitcher
          categoryHeaderOverride={<CategoryHeader category={category} />}
          categoryServices={<Services items={categoryServicesMock} />}
          ymal={
            category.related_products_category?.length && (
              <YMALCarousel items={category.related_products_category} />
            )
          }
          // YMALCarouselSkeleton={<YMALCarouselSkeleton />}
          categorySEOOverride={<CategorySEO category={category} />}
          forceColorSwitcherSpacing={someChildrenHaveColors()}
        />
      )}
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  store => async ctx => {
    const props: TProps = {
      ...(await serverSideTranslations(ctx.locale)),
      error: null,
      banner: null,
      category: null,
      categoryItems: [],
      isScandiCategoryItemsUsed: false,
    };
    let notFound = false;

    try {
      const slug = ctx.params.slug as string[];
      const localityParam = ctx.query.locality; // can be used to override the locality header for testing
      const currencyParam = ctx.query?.setCurrency as string;
      const localityHeader =
        (ctx.req.headers.Locality as string) || (ctx.req.headers.locality as string);
      const path = ctx.query.slug as string[];
      const url = path.join('/');
      const desiredLocale = localityParam ? localityParam.toString().toUpperCase() : localityHeader;
      const currency =
        (isValidCurrencyCode(currencyParam) && currencyParam) ||
        getCookie(COOKIE_CURRENCY_NAME, ctx) ||
        getCookie(COOKIE_CURRENCY_PREF, ctx) ||
        getCurrencyCode(desiredLocale);
      const ctxWithCurrency = {
        ...ctx,
        currency,
      };

      const {
        banner,
        category,
        categoryItems,
        isScandiCategoryItemsUsed,
        redirectUrl,
        notFound: categoryNotFound,
        error,
      } = await getCategory(apolloConfig, slug.join('/'), ctxWithCurrency);

      store.dispatch(updateCategory(category));

      if (redirectUrl) {
        return {
          redirect: {
            destination: getCategoryLink(redirectUrl, ctx.locale),
            permanent: false,
          },
        };
      }

      props.error = error;
      props.banner = banner;
      props.category = category;
      props.categoryItems = categoryItems;
      props.isScandiCategoryItemsUsed = isScandiCategoryItemsUsed;
      notFound = categoryNotFound;

      try {
        const resolvedCategory = await publicQuery<IUrlResolverResponse, IUrlResolverRequest>(
          apolloConfig(ctx.locale as LanguageCode),
          {
            query: UrlResolver,
            variables: {
              url,
            },
          },
          'UrlResolver',
          ctx,
        ).then((res: any) => res.data.urlResolver);

        const resolvedCategoryArray = Object.entries(resolvedCategory.language_urls);

        props.languageUrls = resolvedCategoryArray.map(([key, value]) => ({
          href: key.replace('url_', ''),
          value: value as string,
        }));
      } catch (urlResolverError) {
        console.error('Error resolving category URL:', urlResolverError);
      }
    } catch (err) {
      console.error('Error in getServerSideProps for category page:', err);
      props.error = err instanceof Error ? err.message : 'An unexpected error occurred';
      notFound = true;
    }

    return { props, notFound };
  },
);

export default CategoryPage;
